<script lang="ts" setup>
import { computed, ref, watch, onUnmounted, onMounted } from "vue";
import { ElMessage, ElMessageBox } from "element-plus";
import { EntityDefinition, EntityDefinitionStatus, HomePageType } from "~/api";
import { showError } from "~/utils/handleAPIError";
import { apiClient } from "~/utils/apiClient";
import { CopyDocument } from "@element-plus/icons-vue";
import EnumTag from "~/components/EnumTag.vue";
import { useMyInfoStore } from "~/stores/MyInfoStore";
import { useStorage } from "@vueuse/core";
import { useEntityDefinitionStore } from "~/stores/EntityDefinitionStore";
import { useOrgInfoStore } from "~/stores/OrgInfoStore";
import { CustomEnumDescriptions } from "~/utils/enumDescriptions";
import { emptyGuid } from "~/utils/constants";
const myInfoStore = useMyInfoStore();
const orgInfoStore = useOrgInfoStore();
const entityDefinitionStore = useEntityDefinitionStore();

const entityDefinition = defineModel<EntityDefinition>();
const editing = ref(true);

const availableParents = computed(() => {
  // filter out the current entity from the list of available parents

  // TODO: filter out the children of the current entity from the list of available parents
  return entityDefinitionStore.EntitiesDefinitionList.filter(
    (e) => e.guid !== entityDefinition.value?.guid,
  ).map((e) => {
    return {
      ...e,
      key: e.guid ?? "",
      label: e.name ?? "",
      value: e.guid ?? "",
    };
  });
});

const emit = defineEmits<{
  (e: "closeDrawer"): void;
  (e: "updated", value: EntityDefinition): void;
  (e: "added", value: EntityDefinition): void;
  (e: "deleted", value: EntityDefinition): void;
}>();
const publicItem = ref<boolean>();
const copiedItemStore = useStorage("editing-copied-item", false, localStorage);

const parentGuid = ref<string>("");
const hasParent = ref<boolean>(false);
const homePageWorkflowDefinitionGuid = ref<string>("");
const homePage = ref<HomePageType>(HomePageType.DEFAULT);
const isCrmEntity = ref<boolean>(false);
const crmMenuOrder = ref<number | null>(null);

watch(
  () => entityDefinition.value, // trigger when loaded
  () => {
    editing.value =
      !entityDefinition.value?.guid || copiedItemStore.value ? true : false;
    parentGuid.value = entityDefinition.value?.parentEntityDefinitionGuid ?? "";
    homePageWorkflowDefinitionGuid.value =
      entityDefinition.value?.homePageWorkflowDefinitionGuid ?? "";
    homePage.value = entityDefinition.value?.homePage ?? HomePageType.DEFAULT;
    hasParent.value = !!parentGuid.value;
    publicItem.value = !!entityDefinition.value?.madePublicAt;
    crmMenuOrder.value = entityDefinition.value?.crmMenuOrder ?? null;
    isCrmEntity.value = crmMenuOrder.value !== null;
  },
);

const isUsable = computed(() => {
  return entityDefinition.value?.guid !== emptyGuid;
});

const toggleParentSelection = () => {
  if (!hasParent.value) {
    parentGuid.value = "";
  }
};

const toggleCrmEntity = () => {
  if (!isCrmEntity.value) {
    crmMenuOrder.value = null;
  } else {
    // Default to 0 when enabling CRM entity
    crmMenuOrder.value = 0;
  }
};

const saveItem = async () => {
  if (!entityDefinition.value) {
    return;
  }

  // apply selectable guids
  entityDefinition.value.parentEntityDefinitionGuid = parentGuid.value || null;
  entityDefinition.value.homePageWorkflowDefinitionGuid =
    homePageWorkflowDefinitionGuid.value || null;
  entityDefinition.value.homePage = homePage.value || undefined;
  entityDefinition.value.crmMenuOrder = isCrmEntity.value
    ? crmMenuOrder.value
    : null;

  const guid = entityDefinition.value.guid;
  if (!guid || guid === emptyGuid) {
    entityDefinition.value.sysOrgGuid = myInfoStore.currentSysOrgGuid;
    if (entityDefinition.value.formDefinition) {
      entityDefinition.value.formDefinition.sysOrgGuid =
        myInfoStore.currentSysOrgGuid;
    }
    await apiClient.entityDefinition
      .createEntityDefinition(entityDefinition.value)
      .then((ed) => {
        ElMessage.success("Saved");
        emit("added", ed);
        // handleCloseDrawer();
      })
      .catch(showError);
  } else {
    await apiClient.entityDefinition
      // otherwise, we just update it
      .updateEntityDefinition(guid, entityDefinition.value)
      .then((ed) => {
        ElMessage.success("Saved");
        copiedItemStore.value = false;
        emit("updated", ed);
      })
      .catch(showError);
  }
};

const handleUpdateItemPrivacy = async (val: string | number | boolean) => {
  await ElMessageBox.confirm(
    `This will make this entity ${publicItem.value ? "visible to everyone" : "hidden from everyone outside your organization"}. Continue?`,
    `Make entity ${publicItem.value ? "Public" : "Private"}`,
    {
      confirmButtonText: "Confirm",
      cancelButtonText: "Cancel",
      closeOnPressEscape: false,
      closeOnClickModal: false,
      showClose: false,
      type: "warning",
    },
  )
    .then(async () => {
      if (!entityDefinition.value) {
        showError("Compliance item not found");
        return;
      }
      if (!entityDefinition.value) {
        showError("Compliance item not found");
        return;
      }
      if (val) {
        entityDefinition.value.madePublicAt = new Date().toISOString();
      } else {
        entityDefinition.value.madePublicAt = undefined;
      }
      await saveItem();
    })
    .catch(() => {
      publicItem.value = !publicItem.value;
      ElMessage({
        type: "info",
        message: "Canceled",
      });
    });
};

const handleMakeCopy = async () => {
  if (!entityDefinition.value) {
    showError("Compliance item not found");
    return;
  }
  const newItem = { ...entityDefinition.value };

  if (!newItem.formDefinition) {
    showError("Compliance item doesn't have a form definition");
    return;
  }
  // @ts-expect-error - we want guid to be set by the server
  newItem.guid = undefined;
  newItem.name = `${newItem.name} (Copy)`;
  newItem.madePublicAt = undefined;
  // @ts-expect-error - we want guid to be set by the server
  newItem.formDefinition.guid = undefined;
  newItem.formDefinition?.components.forEach((c) => {
    // @ts-expect-error - we want guid to be set by the server
    c.guid = undefined;
  });
  newItem.formDefinition.sysOrgGuid = myInfoStore.currentSysOrgGuid;

  await apiClient.entityDefinition
    .createEntityDefinition(newItem)
    .then((res) => {
      ElMessage.success("Copied");
      copiedItemStore.value = true;
      emit("added", res);
    })
    .catch(showError);
};

const handleDeleteEntityDefinition = async (
  entityDefinitionGuid: string,
  sysOrgGuid: string,
) => {
  ElMessageBox.confirm(
    "Are you sure you want to delete this entity definition?",
    "Delete Entity Definition",
    {
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
      closeOnPressEscape: false,
      closeOnClickModal: false,
      showClose: false,
      type: "warning",
    },
  )
    .then(async () => {
      if (!entityDefinition.value) {
        return;
      }

      await apiClient.entityDefinition
        .deleteEntityDefinition(entityDefinitionGuid, sysOrgGuid)
        .then((res) => {
          ElMessage.success("Entity definition deleted");
          emit("closeDrawer");
          emit("deleted", res);
        })
        .catch(showError);
    })
    .catch(async () => {
      // do nothing
    });
};

onMounted(async () => {
  await entityDefinitionStore.load();
  localStorage.setItem("editing-copied-item", JSON.stringify(false));
});

onUnmounted(() => {
  localStorage.setItem("editing-copied-item", JSON.stringify(false));
});
</script>
<template>
  <div v-if="myInfoStore.hasSetupBasicRole" class="edit-buttons">
    <el-button v-if="!editing" type="success" @click="editing = true">
      Edit
    </el-button>

    <el-button v-if="editing" plain type="danger" @click="editing = false"
      >Cancel</el-button
    >
    <el-button v-if="editing" type="success" @click="saveItem">Save</el-button>
  </div>
  <br />
  <br />
  <div v-if="entityDefinition" class="compliance-item-details">
    <section class="section core">
      <table>
        <tbody>
          <tr>
            <td>Entity Name</td>
            <td>
              <el-input
                v-if="editing"
                v-model="entityDefinition.name"
                :autofocus="copiedItemStore"
              />
              <span v-else>{{ entityDefinition.name }}</span>
            </td>
          </tr>
          <tr>
            <td>Entity Name (Plural)</td>
            <td>
              <el-input
                v-if="editing"
                v-model="entityDefinition.namePlural"
                :autofocus="copiedItemStore"
              />
              <span v-else>{{ entityDefinition.namePlural }}</span>
            </td>
          </tr>
          <tr>
            <td>Icon</td>

            <td>
              <div v-if="editing">
                <div class="mdiInfo">
                  Enter the name of any icon from the
                  <a
                    href="https://pictogrammers.com/library/mdi/"
                    target="mdiIcons"
                    >MDI Icon Library</a
                  >
                </div>
                <el-input
                  v-model="entityDefinition.icon"
                  placeholder="Enter icon name"
                >
                </el-input>
              </div>
              <div class="iconPreview">
                <i :class="`mdi mdi-${entityDefinition.icon}`"></i>
              </div>
            </td>
          </tr>
          <tr>
            <td>
              <span v-if="hasParent"> Parent entity </span>
              <span v-else> Has a parent entity? </span>
            </td>
            <td>
              <div v-if="editing">
                <el-switch
                  v-model="hasParent"
                  active-text="Parent"
                  inactive-text="No parent"
                  @change="toggleParentSelection"
                />
                <el-select
                  v-if="hasParent"
                  v-model="parentGuid"
                  value-on-clear=""
                  placeholder="Select a parent entity"
                  clearable
                >
                  <el-option
                    v-for="parent in availableParents"
                    :key="parent.key"
                    :label="parent.label"
                    :value="parent.value"
                  />
                </el-select>
              </div>
              <span v-else>
                {{
                  availableParents.find((e) => e.value === parentGuid)?.name ??
                  "No"
                }}
              </span>
            </td>
          </tr>
          <tr>
            <td>
              <span v-if="isCrmEntity"> CRM Menu Order </span>
              <span v-else> Show in CRM Section? </span>
            </td>
            <td>
              <div v-if="editing">
                <el-switch
                  v-model="isCrmEntity"
                  active-text="CRM Entity"
                  inactive-text="Regular Entity"
                  @change="toggleCrmEntity"
                />
                <el-input-number
                  v-if="isCrmEntity"
                  v-model="crmMenuOrder"
                  :min="0"
                  :max="999"
                  placeholder="Menu order"
                  style="margin-left: 1em; width: 120px"
                />
              </div>
              <span v-else>
                <template v-if="crmMenuOrder !== null">
                  Yes (Order: {{ crmMenuOrder }})
                </template>
                <template v-else> No </template>
              </span>
            </td>
          </tr>
          <tr>
            <td>Home Page Style</td>
            <td>
              <div v-if="editing">
                <el-select v-model="homePage">
                  <el-option
                    v-for="key in HomePageType"
                    :key="key"
                    :label="key"
                    :value="key"
                  />
                </el-select>
              </div>

              <span v-else>
                <EnumTag
                  :model-value="homePage ?? HomePageType.DEFAULT"
                  size="large"
                  type="homePageType"
                />
              </span>
            </td>
          </tr>
          <tr v-if="homePage === HomePageType.WORKFLOWS">
            <td></td>
            <td>
              <span>Workflows to Show: </span>
              <div v-if="editing">
                <el-select v-model="homePageWorkflowDefinitionGuid">
                  <el-option
                    v-for="wd in orgInfoStore.MyOrgInfo?.workflowDefinitionDtos"
                    :key="wd.guid"
                    :label="wd.name"
                    :value="wd.guid"
                  />
                </el-select>
              </div>

              <span v-else>
                <enumTag
                  :model-value="homePageWorkflowDefinitionGuid"
                  size="large"
                  type="customDescriptions"
                  :custom-enum-descriptions="
                    orgInfoStore.MyOrgInfo?.workflowDefinitionDtos?.reduce(
                      (acc, wd) => ({
                        ...acc,
                        [wd.guid]: {
                          label: wd.name,
                        },
                      }),
                      {} as CustomEnumDescriptions,
                    )
                  "
                />
              </span>
            </td>
          </tr>
          <tr>
            <td>Status</td>
            <td>
              <el-select v-if="editing" v-model="entityDefinition.status">
                <el-option
                  v-for="(key, value) in EntityDefinitionStatus"
                  :key="key"
                  :label="key"
                  :value="value"
                />
              </el-select>

              <span v-else>
                <EnumTag
                  :model-value="entityDefinition.status"
                  size="large"
                  type="complianceItemStatus"
                />
              </span>
            </td>
          </tr>
          <tr>
            <td>
              Make Entity Public
              <el-tooltip
                content="Other organizations on this website will be able to use this entity"
                placement="top"
              >
                <el-icon>
                  <el-icon-info-filled />
                </el-icon>
              </el-tooltip>
            </td>
            <td>
              <el-checkbox
                v-if="isUsable && editing"
                v-model="publicItem"
                @change="
                  async ($event: boolean) =>
                    await handleUpdateItemPrivacy($event)
                "
              >
                <span>{{}}</span>
              </el-checkbox>
              <span v-else>
                <el-icon v-if="entityDefinition.madePublicAt"
                  ><el-icon-check
                /></el-icon>
                <el-icon v-else><el-icon-close /></el-icon>
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </section>
    <section class="section delete">
      <div>
        <el-button
          v-if="editing && entityDefinition.guid !== emptyGuid"
          :disabled="entityDefinition.hasEntityInstances"
          @click="
            handleDeleteEntityDefinition(
              entityDefinition.guid,
              myInfoStore.currentSysOrgGuid,
            )
          "
          >Delete Entity Definition</el-button
        >
      </div>
    </section>

    <el-button
      v-if="!editing && entityDefinition.guid"
      class="copy-btn"
      :icon="CopyDocument"
      @click="handleMakeCopy"
    >
      Make Copy
    </el-button>
  </div>
</template>

<style lang="less">
.item-designer,
.el-tabs,
.el-tabs__content,
.el-tab-pane,
.compliance-item-details {
  height: 100%;
}
.compliance-item-details {
  display: flex;
  flex-direction: column;
  overflow-y: auto;

  .edit-buttons {
    display: flex;
    .el-switch + .el-button {
      margin-left: 12px;
    }
  }

  .copy-btn {
    justify-self: end;
    align-self: end;
    margin-top: 1em;
  }

  .expiry-date {
    display: flex;
    align-items: center;
    gap: 1em;

    .el-input-number {
      width: 3em;
    }
    .el-select {
      width: 7em;
    }
  }

  table {
    td {
      padding: 0.5em;
      border-bottom: 1px solid #ebeef5;
    }
    td:first-child {
      white-space: nowrap;
    }
  }
  .mdiInfo {
    margin: 0 0 5px 10px;
    font-size: 90%;
    font-style: italic;
  }
  .iconPreview {
    margin: 10px 0 0 10px;

    i.mdi {
      vertical-align: middle;
      font-size: 2.5em;
      line-height: 1em;
    }
  }
}
</style>
